import Flex from 'components/Flex';
import Gallery from 'components/Gallery';
import Text from 'components/Text';
import useGallery from 'hooks/useGallery';
import useTheme from 'hooks/useTheme';
import { ReactComponent as FileIcon } from 'icons/file.svg';
import { IMAGE_EXTENSIONS } from 'libs/constants';
import { downloadFile } from 'libs/utils';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { IUserMainInfo } from 'components/User/Info';
import styled from 'styled-components';
import FileBlock, { IFileBlockItem, IFileView } from './FileBlock';
import useReloadedFiles from './useReloadedFiles';
import { TFetchFiles } from './utils';

export interface IFilePath {
  id?: number;
  identifier: string;
  url: string;
  url_live_to?: string;
  name?: string;
  profile?: IUserMainInfo;
  created_at?: string;
  is_main?: boolean;
  title?: string;
}

export type TAttachment = IFilePath | File;

export interface IAttachment {
  className?: string;
  files: TAttachment[];
  handleRemove?: (newValues: TAttachment[]) => void;
  handleDownload?: (src: string, fileName: string) => void;
  onViewChange?: (index: number) => void;
  label?: string;
  editMode?: boolean;
  fileView?: FunctionComponent<IFileView>;
  wrapperView?: FunctionComponent;
  setMainPhotoIndex?: (name: string, value: number | string) => void;
  mainPhotoIndex?: string | number;
  isShowTooltip?: boolean;
  fetchFiles?: TFetchFiles;
}

export const getFileNameFromPath = (file: IFilePath): string =>
  (file.url.split('/').pop() || '').split('?')[0] || '';

export const getFileCaption = (file: TAttachment): string => {
  if (file instanceof File || file.name) {
    return file.name || '';
  } else {
    return getFileNameFromPath(file);
  }
};

export const getFileName = (file: TAttachment): string => {
  if (file instanceof File) {
    return file.name;
  }
  return getFileNameFromPath(file);
};

const Attachments: FunctionComponent<IAttachment> = ({
  className,
  files,
  handleRemove = () => false,
  handleDownload,
  label,
  editMode = false,
  isShowTooltip = true,
  fileView: FileView,
  wrapperView: WrapperView = Wrapper,
  setMainPhotoIndex,
  mainPhotoIndex,
  onViewChange,
  fetchFiles,
}) => {
  const theme = useTheme();
  const { currentImage, viewerIsOpen, openGallery, closeGallery } = useGallery();
  const { files: currentFiles, reloadFile } = useReloadedFiles(files, fetchFiles);

  const allFiles: IFileBlockItem[] = useMemo(() => {
    let imageCounter = 1;
    return currentFiles.map((file, index: number) => {
      const id = file instanceof File ? file.name : file.id;
      const caption: string = getFileCaption(file);
      const title = file instanceof File ? caption : file.title;
      const profile = file instanceof File ? undefined : file.profile;
      const createdAt = file instanceof File ? undefined : file.created_at;
      const isMain =
        mainPhotoIndex !== undefined
          ? file instanceof File
            ? mainPhotoIndex === caption
            : mainPhotoIndex === id
          : //@ts-ignore
            file?.is_main;
      const filename: string = getFileName(file);
      const ext: string = filename.split('.').pop() || '';
      let imageIndex;
      if (IMAGE_EXTENSIONS.includes(ext.toLowerCase())) {
        imageIndex = imageCounter;
        imageCounter += 1;
      }
      return {
        id,
        caption,
        download: !Boolean(file instanceof File),
        profile,
        createdAt,
        ext,
        imageIndex,
        index,
        isMain,
        title,
        src:
          file instanceof File
            ? URL.createObjectURL(file)
            : `${process.env.REACT_APP_IMAGE_PREFIX}${file.url}`,
      };
    });
  }, [currentFiles, mainPhotoIndex]);

  const removeFile = useCallback(
    (index: number) => {
      handleRemove([...files.slice(0, index), ...files.slice(index + 1)]);
    },
    [files]
  );

  const views = useMemo(() => allFiles.filter((item) => item.imageIndex), [allFiles]);

  const handleSetMainPhoto = useCallback(
    (id: any) => () => {
      if (setMainPhotoIndex) setMainPhotoIndex('main_photo_index', id);
    },
    [openGallery]
  );

  const openGalleryWithReload = useCallback(
    async (imageIndex: number) => {
      const file = await reloadFile(views[imageIndex].index);
      if (file) openGallery(imageIndex);
    },
    [views, reloadFile, openGallery]
  );

  const handleViewChange = useCallback(
    (imageIndex: number) => {
      const fileIndex = views[imageIndex].index;
      onViewChange?.(fileIndex);
      reloadFile(fileIndex);
    },
    [views, onViewChange, reloadFile]
  );

  const downloadWithReload = useCallback(
    async (src: string, fileName: string) => {
      if (handleDownload) return handleDownload(src, fileName);

      const file = await reloadFile(allFiles.findIndex((item) => item.src === src));
      if (!file) return;

      const url = file instanceof File ? src : `${process.env.REACT_APP_IMAGE_PREFIX}${file.url}`;
      downloadFile({ url });
    },
    [handleDownload, allFiles, reloadFile]
  );

  return (
    <div hidden={!files || !files.length} className={className}>
      <Flex direction="column">
        {label && (
          <Flex alignItems="center" className="mb4">
            <FileIcon />
            <Text size="xs" weight="bold" color={theme.colors.dark} className="ml12">
              {label}
            </Text>
          </Flex>
        )}
        <WrapperView>
          {allFiles.map((item, index) => (
            <FileBlock
              key={index}
              item={item}
              editMode={editMode}
              handleDownload={downloadWithReload}
              removeFile={removeFile}
              openGallery={openGalleryWithReload}
              setMainPhoto={handleSetMainPhoto(item.id)}
              isShowTooltip={isShowTooltip}
            >
              {FileView}
            </FileBlock>
          ))}
        </WrapperView>
      </Flex>
      {views && viewerIsOpen && (
        <Gallery
          views={views}
          currentImage={currentImage}
          viewerIsOpen={viewerIsOpen}
          closeGallery={closeGallery}
          onViewChange={handleViewChange}
        />
      )}
    </div>
  );
};

export default Attachments;

const Wrapper = styled(Flex)`
  flex-flow: wrap;
`;
